import ChatWindowHeader from '../chat-window-header/ChatWindowHeader';
import useInfo from '../../hooks/useInfo';
import ProductivityGainModal from '../productivity-gain-modal/ProductivityGainModal';
import useMessagesState from '../../features/chat/hooks/useMessagesState';
import useScrollToBottom from '../../hooks/useScrollToBottom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoute } from '../../router/Routing';
import { TestIds } from '../../mocks/ids';
import { Chat } from '../../features/chat';
import { ChatViewLayoutProps } from '../../layouts/chat-view-layout/ChatViewLayout';
import { useChat } from '../../contexts/chat/ChatContext';
import { useLayout } from '../../contexts/layout/LayoutContext';
import styles from './ChatWindow.module.scss';
import { ObsoleteNotice } from '../../ui/obsolete-notice/ObsoleteNotice';
import useLabels from '../../hooks/useLabels';
import { advancedAssistantKey, defaultPersonaKey } from '../../constants/consts';
import { useAdaptive } from '../../contexts/adaptive/AdaptiveContext';
import HelpCasePanel from './help-case-panel/HelpCasePanel';
import { ReactComponent as ArrowDownIcon } from '@danfoss/mosaic-icons/dist/svg/arrow-down-outline.svg';

export const HELP_CASE_PANEL_COLLAPSED_STORAGE_KEY = 'chat:help-case-panel:collapsed';

const getStoredHelpCasePanelCollapsedState = () => {
    try {
        return localStorage.getItem(HELP_CASE_PANEL_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const setStoredHelpCasePanelCollapsedState = (isCollapsed: boolean) => {
    try {
        localStorage.setItem(HELP_CASE_PANEL_COLLAPSED_STORAGE_KEY, String(isCollapsed));
    } catch {
        // Ignore storage errors (private mode/quota) and keep in-memory state.
    }
};

const ChatWindow: React.FC<ChatViewLayoutProps> = ({ chatId = '', onOpenSidebar, onReloadChats }) => {
    const navigate = useNavigate();
    const labels = useLabels();
    const infoProps = useInfo();
    const { catalogFavorites } = useAdaptive();
    const { prompt, createChat, persistent, updateChatMetadata, submitProductivityGain } = useChat();
    const { isRightSidebarVisible, isLeftSidebarVisible, expandLeftSidebar, collapseLeftSidebar, hideLeftSidebar } = useLayout();

    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollToBottom, scrollToLatestPrompt, scrollToBottomWithDelay, hasContentBelow } = useScrollToBottom(containerRef);

    const chatProps = useMessagesState({ chatId, infoProps, scrollToBottomWithDelay, prompt, createChat, persistent });
    const { chatHistoryError, isLoadingHistory, renderHistoryError, chat, modelMetadata, messages: chatMessages } = chatProps.chat as any;
    const chatTitle = useMemo(() => chat?.session?.subject || '', [chat]);
    const latestBotResponse = useMemo(() => {
        return Array.isArray(chatMessages)
            ? [...chatMessages].reverse().find((message) => message?.type === 'ai' && !message?.isStreaming)
            : undefined;
    }, [chatMessages]);

    const [showProductivityModal, setShowProductivityModal] = useState(false);
    const [isHelpdeskModalOpen, setIsHelpdeskModalOpen] = useState(false);
    const [isHelpPanelVisible, setIsHelpPanelVisible] = useState(false);
    const [isHelpPanelCollapsed, setIsHelpPanelCollapsed] = useState(getStoredHelpCasePanelCollapsedState);

    const setHelpPanelCollapsedState = useCallback((isCollapsed: boolean) => {
        setIsHelpPanelCollapsed(isCollapsed);
        setStoredHelpCasePanelCollapsedState(isCollapsed);
    }, []);

    useEffect(() => {
        if (latestBotResponse) {
            setIsHelpPanelVisible(true);
        }
    }, [latestBotResponse]);

    const helpPanelRef = useRef<HTMLDivElement>(null);
    // Collapse help panel when window/browser is minimized
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {if (
            helpPanelRef.current &&
            !helpPanelRef.current.contains(event.target as Node)) {
                setHelpPanelCollapsedState(true);
            }
        };
        const handleWindowBlur = () => {
            setHelpPanelCollapsedState(true);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                setHelpPanelCollapsedState(true);
            }
        };

        window.addEventListener('blur', handleWindowBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener('blur', handleWindowBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [setHelpPanelCollapsedState]);

    const onCloseProductivityModal = useCallback(() => setShowProductivityModal(false), []);
    const onOpenProductivityModal = useCallback(() => setShowProductivityModal(true), []);
    const onSubmitProductivityGain = useCallback(async (productivityGain: number) => {
        if (!chatId) return;
        await submitProductivityGain(chatId, productivityGain);
        setShowProductivityModal(false);
        await onReloadChats?.(true);
    }, [chatId, onReloadChats, submitProductivityGain]);

    const onRenameChatHandler = useCallback(async (newName: string) => {
        if (!chatId) return;
        const result = await updateChatMetadata(chatId, { subject: newName });
        await onReloadChats?.(true);
        return result;
    }, [chatId, onReloadChats, updateChatMetadata]);

    const onNavigateToNewChat = useCallback(() => {
        const route = infoProps.getPersonaFromKey(chat?.session.persona ?? "")?.route;
        navigate(route ? '/' + route : AppRoute.chat);
    }, [navigate, infoProps, chat]);

    const handleChatViewKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        // Ctrl/Cmd + Shift + K will start a new chat
        const holdsCtrlOrCmd = e.ctrlKey || e.metaKey;
        if (!holdsCtrlOrCmd) return;
        const holdsShift = e.shiftKey;
        if (!holdsShift) return;
        if (e.key.toLowerCase() !== 'k') return;

        e.preventDefault();
        e.stopPropagation();
        onNavigateToNewChat();
    }, [onNavigateToNewChat]);

    const onCaseCreatedRef = useRef<(() => void) | undefined>(undefined);

    const handleCreateCase = useCallback((onCaseCreated?: () => void) => {
        onCaseCreatedRef.current = onCaseCreated;
        setIsHelpPanelVisible(true);
        setIsHelpdeskModalOpen(true);
    }, []);

    const handleCreateCaseFromPanel = useCallback(() => handleCreateCase(), [handleCreateCase]);

    const handleCaseCreated = useCallback(() => {
        onCaseCreatedRef.current?.();
        onCaseCreatedRef.current = undefined;
    }, []);

    const handleCloseHelpdeskModal = useCallback(() => setIsHelpdeskModalOpen(false), []);

    const handleDismissHelpPanel = useCallback(() => {
        setHelpPanelCollapsedState(true);
    }, [setHelpPanelCollapsedState]);

    const handleExpandHelpPanel = useCallback(() => {
        setHelpPanelCollapsedState(false);
    }, [setHelpPanelCollapsedState]);

    const chatTitleLimit = chatTitle?.length > 100 ? chatTitle.slice(0, 100) + "..." : chatTitle;

    if (!!chatHistoryError) return renderHistoryError;

    return <Chat.ContextProvider value={chatProps} config={{ chat: { onOpenHelpdeskCase: handleCreateCase } }}>
        <div data-testid={TestIds.chatWindow} className={styles['chat-window']} onKeyDown={handleChatViewKeyDown} tabIndex={0}>
            <div className={styles['chat-window-wrapper']}>
                <ChatWindowHeader
                    isRightSidebarVisible={isRightSidebarVisible}
                    isLeftSidebarVisible={isLeftSidebarVisible}
                    expandLeftSidebar={() => {
                        expandLeftSidebar();
                        // showLeftSidebar();
                    }}
                    collapseLeftSidebar={() => {
                        collapseLeftSidebar();
                        hideLeftSidebar();
                    }}
                    initialTitle={chatTitle}
                    onBurgerClick={onOpenSidebar}
                    onClockClick={onOpenProductivityModal}
                    onCreateNewChat={onNavigateToNewChat}
                    onRenameChat={onRenameChatHandler}
                    disableInteractions={isLoadingHistory}
                />
                {chat?.session?.persona === advancedAssistantKey && (
                    <div className={styles['notice-container']}>
                        <ObsoleteNotice
                            message={labels.advancedObsoleteDescription}
                            buttonLabel={labels.basicChatAsistantTitle}
                            onClick={() => {
                                catalogFavorites.swapPinnedPersonas?.(advancedAssistantKey, defaultPersonaKey);
                                navigate(AppRoute.basicAssistant);
                            }}
                        />
                    </div>
                )}
                <div className={styles['conversation-viewport']}>
                    <Chat.Messages ref={containerRef} scrollToBottomWithBuffer={scrollToLatestPrompt} />
                    {hasContentBelow && (
                        <div className={styles['scroll-cue']}>
                            <button
                                type="button"
                                className={styles['scroll-to-bottom']}
                                aria-label={labels.scrollToBottom}
                                title={labels.scrollToBottom}
                                onClick={scrollToBottom}
                            >
                                <ArrowDownIcon aria-hidden="true" focusable="false" width={24} height={24} />
                            </button>
                        </div>
                    )}
                </div>
                <div ref={helpPanelRef} className={styles['help-panel-container']}>
                    <HelpCasePanel
                        chatTitle={chatTitleLimit}
                        isVisible={isHelpPanelVisible}
                        isCollapsed={isHelpPanelCollapsed}
                        isModalOpen={isHelpdeskModalOpen}
                        onCloseModal={handleCloseHelpdeskModal}
                        onCreateCase={handleCreateCaseFromPanel}
                        onCaseCreated={handleCaseCreated}
                        onDismiss={handleDismissHelpPanel}
                        onExpand={handleExpandHelpPanel}
                    />
                </div>
            </div>

            <Chat.Input />

            <ProductivityGainModal initialProductivityGain={modelMetadata.productivityGain} chatId={chatId} isOpen={showProductivityModal} onClose={onCloseProductivityModal} onSubmit={onSubmitProductivityGain} />
        </div>
    </Chat.ContextProvider>;
};

export default ChatWindow;
